import { z } from 'zod';

/*
 * Modelo de um app da fonte, lido para gerar a proposta de contexto (fase 3).
 * Só metadados: layout, script, tabelas e campos com contagens, itens mestres e a definição dos
 * gráficos das pastas. Nenhum valor de campo e nenhuma linha de dado entram aqui.
 */

export const ModelFieldSchema = z.object({
  name: z.string(),
  /** Marcas do engine: $key, $numeric, $integer, $text, $date, $timestamp, $ascii, $hidden... */
  tags: z.array(z.string()),
  /** Valores distintos do campo (contagem, nunca os valores). */
  distinct: z.number().int(),
  tables: z.array(z.string()),
  /** Campos derivados do calendário automático (Ano, Mês...), quando o campo tem. */
  derived: z.array(z.string()).default([]),
});
export type ModelField = z.infer<typeof ModelFieldSchema>;

export const ModelTableSchema = z.object({
  name: z.string(),
  rows: z.number().int(),
  fields: z.array(z.string()),
  /** Tabela de chave sintética ($Syn) criada pelo engine. */
  synthetic: z.boolean(),
  /** Tabela solta: o engine cortou a associação para quebrar uma referência circular. */
  loose: z.boolean(),
  comment: z.string().nullable(),
});
export type ModelTable = z.infer<typeof ModelTableSchema>;

export const NumberFormatSchema = z.object({ type: z.string(), decimals: z.number().int().nullable(), format: z.string().nullable() });

export const ModelMeasureSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  expression: z.string(),
  label: z.string().nullable(),
  label_expression: z.string().nullable(),
  number_format: NumberFormatSchema.nullable(),
  tags: z.array(z.string()),
});
export type ModelMeasure = z.infer<typeof ModelMeasureSchema>;

export const ModelDimensionSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  /** Um campo (ou expressão começando com =); vários quando é hierarquia (drill-down) ou cíclica. */
  fields: z.array(z.string()),
  labels: z.array(z.string()),
  label_expression: z.string().nullable(),
  /** N: simples. H: drill-down. C: cíclica. */
  grouping: z.string(),
  tags: z.array(z.string()),
});
export type ModelDimension = z.infer<typeof ModelDimensionSchema>;

/** Dimensão ou medida de um gráfico. library_id aponta para o item mestre, quando é um. */
export const VizDimensionSchema = z.object({
  library_id: z.string().nullable(),
  fields: z.array(z.string()),
  labels: z.array(z.string()),
  label_expression: z.string().nullable(),
});
export const VizMeasureSchema = z.object({
  library_id: z.string().nullable(),
  expression: z.string(),
  label: z.string().nullable(),
  label_expression: z.string().nullable(),
});

export const ModelVisualizationSchema = z.object({
  id: z.string(),
  type: z.string(),
  title: z.string(),
  /** Id do contêiner que guarda este gráfico, quando está dentro de um. */
  container: z.string().nullable(),
  dimensions: z.array(VizDimensionSchema),
  measures: z.array(VizMeasureSchema),
});
export type ModelVisualization = z.infer<typeof ModelVisualizationSchema>;

export const ModelSheetSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  visualizations: z.array(ModelVisualizationSchema),
});
export type ModelSheet = z.infer<typeof ModelSheetSchema>;

export const AppModelSchema = z.object({
  app_id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  last_reload_at: z.string().nullable(),
  /** Script de carga, quando o usuário pode ler; null quando o engine recusa. */
  script: z.string().nullable(),
  tables: z.array(ModelTableSchema),
  fields: z.array(ModelFieldSchema),
  measures: z.array(ModelMeasureSchema),
  dimensions: z.array(ModelDimensionSchema),
  sheets: z.array(ModelSheetSchema),
  /** Variáveis do app: nome e definição (nunca o valor calculado). O agente usa em condição de filtro, como <=$(vMesBase). */
  variables: z.array(z.object({ name: z.string(), definition: z.string() })).default([]),
});
export type AppModel = z.infer<typeof AppModelSchema>;
